import { Injectable } from '@angular/core';
import { Subject, Subscription, delayWhen, map, of } from 'rxjs';
import { modifiedInterval } from '../utilities/ZoneUtils';

export enum MessageDuration {
  Permanent = 0,
  Short = 4000,
  Long = 10000,
}

export enum ToastMessageTarget {
  WB_Toolbar,
  Canvas_Item,
}

export interface ToastMessageOptions {
  message: string;
  icon?: string;
  duration: MessageDuration;
  target: ToastMessageTarget;
  id?: string;
  cta?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WbToastMessageService {
  private _toastsLimit = 1;
  dismissToastSubject = new Subject<ToastMessageOptions[]>();
  toastMessageSubject = new Subject<ToastMessageOptions[] | undefined>();

  toastMessageOptions$ = this.toastMessageSubject
    .asObservable()
    .pipe(
      map((options) =>
        options?.filter((option) => !option || option?.target === ToastMessageTarget.WB_Toolbar),
      ),
    );

  toastMessages: ToastMessageOptions[] = [];
  private toastMessagesSubscriptions: Subscription[] = [];

  public set toastsLimit(toastsLimit: number) {
    this._toastsLimit = toastsLimit;
  }

  public show(option: ToastMessageOptions): void {
    if (this._toastsLimit === this.toastMessages.length) {
      this.toastMessages.shift();
    }
    this.toastMessages.push(option);
    this.toastMessageSubject.next([...this.toastMessages]);
    this.subscribeToTimedToasts(option);
  }

  public hide(index?: number) {
    if (index != null) {
      this.destroyTimedToastsSubscriptions(index);
      this.toastMessages.splice(index, 1);
      this.toastMessageSubject.next([...this.toastMessages]);
      return;
    }
    if (this.toastMessages.length > 1) {
      this.toastMessages.shift();
      this.toastMessageSubject.next([...this.toastMessages]);
    } else {
      this.hideAll();
      this.destroyTimedToastsSubscriptions();
    }
  }

  public hideAll() {
    this.toastMessageSubject.next(undefined);
    this.toastMessages = [];
  }

  private subscribeToTimedToasts(option: ToastMessageOptions) {
    const firstTimedToastDuration = this.toastMessages.find(
      (m) => m.duration !== MessageDuration.Permanent,
    )?.duration;

    if (option.duration !== MessageDuration.Permanent && firstTimedToastDuration) {
      const subs = of(option)
        .pipe(delayWhen(() => modifiedInterval(firstTimedToastDuration as number)))
        .subscribe(() => {
          this.hide();
        });
      this.toastMessagesSubscriptions.push(subs);
    }
  }

  private destroyTimedToastsSubscriptions(index?: number) {
    if (index != null && this.toastMessagesSubscriptions[index]) {
      this.toastMessagesSubscriptions[index].unsubscribe();
      this.toastMessagesSubscriptions.splice(index, 1);
      return;
    }
    this.toastMessagesSubscriptions.forEach((subs) => subs.unsubscribe());
  }
}
